import {
  Archive, ArrowLeftRight, Ban, Camera, Check, ChevronDown, ChevronRight,
  Crosshair, Download, Eye, EyeOff, Maximize2, MapPin, Pause, Pencil, Play,
  Plus, RefreshCw, RotateCcw, Square, Trash2, TriangleAlert, Upload, X,
  Zap, type LucideIcon,
} from "lucide-react";

/** The console's icon set — Lucide, at one size, from one place.
 *
 *  Dattatray: *"use lucide icons for delete, archive etc, symbols!"*
 *
 *  WHAT IT REPLACED: emoji and box-drawing characters — 🗑 ⭳ ⭱ 🗄 ✕ ▶ ⏸ ✎ ↩ —
 *  typed directly into the JSX. That is three separate problems, and only the
 *  first is taste:
 *
 *    THEY ARE FONTS, not graphics. 🗑 and 📷 render as the platform's colour
 *    emoji on Windows, flat glyphs on Linux, and something else again on a
 *    phone — so the same button is a different picture per machine, and none
 *    of them match the Lucide icons already in the sidebar.
 *    ✕ (U+2715) and ⭳ (U+2B33) are not emoji at all: they are rare symbol
 *    glyphs that many fonts simply lack, and a missing glyph renders as a
 *    box.
 *
 *    THEY DO NOT INHERIT. An emoji ignores `currentColor`, so a red Delete
 *    button had a black-and-white bin on it and the colour said nothing.
 *
 *    THEY HAVE NO SIZE. A glyph is sized by font-size and sits on the text
 *    baseline, so icons drifted a pixel or two between buttons.
 *
 *  Lucide was ALREADY a dependency (the sidebar, the theme toggle and the
 *  plans matrix use it) — so this is not a new library, it is the console
 *  finally using one icon set instead of two.
 *
 *  ONE SIZE, ONE STROKE. 16px at stroke 2 matches the sidebar. `size` exists
 *  for the few places that need a larger mark, and nothing sets colour: an
 *  icon takes `currentColor` from the button it sits in, which is what makes
 *  a red button's bin red.
 */
export const ICONS = {
  // the ones Dattatray named
  delete: Trash2,
  archive: Archive,
  restore: RotateCcw,

  // files
  download: Download,
  upload: Upload,
  camera: Camera,

  // chrome
  close: X,
  edit: Pencil,
  add: Plus,
  expand: ChevronRight,
  collapse: ChevronDown,
  fullscreen: Maximize2,
  refresh: RefreshCw,
  show: Eye,
  hide: EyeOff,

  // running things
  play: Play,
  pause: Pause,
  stop: Square,

  // state
  ok: Check,
  warn: TriangleAlert,
  blocked: Ban,
  live: Zap,
  locate: Crosshair,
  place: MapPin,
  swap: ArrowLeftRight,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 16, className, title }: {
  name: IconName;
  /** 16 matches the sidebar; raise it only for a heading-scale mark */
  size?: number;
  className?: string;
  /** an accessible label — omit for an icon that merely decorates a word */
  title?: string;
}) {
  const C = ICONS[name];
  return (
    <C size={size} strokeWidth={2} aria-hidden={title ? undefined : true}
      aria-label={title} className={className}
      // `currentColor` is the point: the icon takes the button's colour, so a
      // red Delete gets a red bin without anyone saying so twice.
      style={{ flexShrink: 0, verticalAlign: "-0.15em" }} />
  );
}
